import { EventEmitter, Injectable, Output } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CommonService } from '../../core/common-service/common.service';
import { AppsettingsProvider } from '../../core/appsettings.provider';
import { MachineDeviceRpcUpdate, MachineDeviceSearchItem, MachineDeviceSearchMetadata, MachineDeviceSearchRequest, MachineDeviceSearchResponse, MachineDeviceSelectionPriceRequest, MachineDeviceSelectionPriceStatusUpdateRequest, MachineDeviceSelectionPriceStatusUpdateResponse, PogDexDiscrepancySearchItem, PogDexDiscrepancySearchMetadata, PogDexDiscrepancySearchRequest, PogDexDiscrepancySearchResponse, RPCDexPriceDiscrepenciesFilterItemsResponse, RPCMachineDeviceCompatibilityItemsRequest, RPCMachineDeviceCompatibilityItemsResponse as RPCMachineDeviceCompatibilityItemsResponse, RPCTrackStatusItem, RPCTrackStatusResponse, RPCTrackStatusSaveRequest, RPCTrackStatusSaveResponse, RPCTrackStatusSummaryResponse, RpcTrackStatusSearchRequest } from './remote-price-change.model';
import { FilterBarService } from '../../shared/filter-bar-service/filter-bar.service';
import { Subject } from 'rxjs';
import { SecurityService } from '../../core/security/security.service';

@Injectable()
export class RemotePriceChangeService {

  appSettings: any;
  previousSearch: MachineDeviceSearchItem;

  machineDeviceSearchItemList: MachineDeviceSearchItem[];
  machineDeviceSearchItemSubject: Subject<MachineDeviceSearchItem[]> = new Subject<MachineDeviceSearchItem[]>();
  machineDeviceSearchMetadataSubject:  Subject<MachineDeviceSearchMetadata> = new Subject<MachineDeviceSearchMetadata>();

  pogDexDiscrepancySearchItemList: PogDexDiscrepancySearchItem[];
  pogDexDiscrepancySearchItemSubject: Subject<PogDexDiscrepancySearchItem[]> = new Subject<PogDexDiscrepancySearchItem[]>();
  pogDexDiscrepancySearchMetadataSubject: Subject<PogDexDiscrepancySearchMetadata> = new Subject<PogDexDiscrepancySearchMetadata>();

  httpErrorResponseSubject: Subject<HttpErrorResponse> = new Subject<HttpErrorResponse>();

  // RPC Maintenance events
  @Output() onRPCUpdateSuccessEventEmitter = new EventEmitter<MachineDeviceSearchResponse>();
  @Output() onRPCUpdateFailureEventEmitter = new EventEmitter();

  // RPC Price Discrepancies events
  @Output() onRPCPriceDiscrepanciesDataReadyEventEmitter = new EventEmitter();
  @Output() onRPCRequestBeginEventEmitter = new EventEmitter();
  @Output() onRPCRequestEndEventEmitter = new EventEmitter();
  @Output() onRPCRequestChangeEventEmitter = new EventEmitter<PogDexDiscrepancySearchItem>();

  constructor(private _http: HttpClient,
    private _common: CommonService,
    private _security: SecurityService,
    private appSettingsProvider: AppsettingsProvider,
    private _filterBarService  : FilterBarService) {
      this.appSettings = this.appSettingsProvider.getSettings();
    }

  Initialize() {
      const costCenterID = Number(this._common.operation.operationNo);
  
      // Get all filter lists and the result set from MachineService API
      this._http.get<MachineDeviceSearchResponse>(
        this.appSettings.urlLocations.deviceManagementURL + 'machinedevicesearchfilter/' + costCenterID)
        .subscribe((data: MachineDeviceSearchResponse) => {       
         const routeList = data.routeLineItems;
         this._filterBarService.nonFilteredRoutes = routeList;
         this._filterBarService.routes.next(routeList);
        },
        (error)=>{
          console.log(error);
         });
  }
  
  onError(error) {
    console.log(error);
    let httpErrorResponse:HttpErrorResponse = error;
    if (httpErrorResponse) {
      this.httpErrorResponseSubject.next(httpErrorResponse);
    }
  }
  
  Search(searchRequest: MachineDeviceSearchRequest) {
    this._http.post<MachineDeviceSearchResponse>(this.appSettings.urlLocations.deviceManagementURL + 'machinedevicesearch', searchRequest)
      .subscribe((data: MachineDeviceSearchResponse) => {
        this.machineDeviceSearchItemSubject.next(data.items.slice());
        this.machineDeviceSearchMetadataSubject.next(data.metadata);
      },
      (error)=> {
        this.onError(error);
      });
  }

  GetMachineDeviceSearch(searchRequest: MachineDeviceSearchRequest) {
    return this._http.post<MachineDeviceSearchResponse>(this.appSettings.urlLocations.deviceManagementURL + 'machinedevicesearch', searchRequest);
  }

  GetRPCDexPriceDiscrepencyFilterData() {
      let costCenterID = Number(this._common.getOperation().operationNo);
      return this._http.get<RPCDexPriceDiscrepenciesFilterItemsResponse>(this.appSettings.urlLocations.deviceManagementURL + 'rpcdexpricediscrepencyfilterdata?costCenterID=' + costCenterID);
  }

  DoPogDexDiscrepancySearch(searchRequest: PogDexDiscrepancySearchRequest) {
    this._http.post<PogDexDiscrepancySearchResponse>(this.appSettings.urlLocations.deviceManagementURL + 'pogdexdiscrepancysearch', searchRequest)
    .subscribe((data: PogDexDiscrepancySearchResponse) => {
      this.pogDexDiscrepancySearchItemSubject.next(data.items.slice());
      this.pogDexDiscrepancySearchMetadataSubject.next(data.metadata);
      this.onRPCPriceDiscrepanciesDataReadyEventEmitter.emit();
    },
    (error)=> {
      console.log("Failed to get price discrepancies. Error=${error}");
      this.onError(error);
    });
  }

  RunUpdateAndPogDexDiscrepancySearch(searchRequest: PogDexDiscrepancySearchRequest) {
    // Requests update of queued status request, and 
    this.sendMachineDeviceSelectionPriceStatusUpdate()
      .subscribe((data: MachineDeviceSelectionPriceStatusUpdateResponse) => {
        console.log("Updated RPC Status", data);
        this.DoPogDexDiscrepancySearch(searchRequest);
      },
      (error)=> {
        console.log("Failed to update RPC Status. Error=${error}");
        this.DoPogDexDiscrepancySearch(searchRequest);
        this.onError(error);
      });
  }

  sendUpdates(updateList: MachineDeviceRpcUpdate[]){
    this._http.post<MachineDeviceSearchResponse>(this.appSettings.urlLocations.deviceManagementURL + 'machinedeviceupdate', updateList)
      .subscribe((data: MachineDeviceSearchResponse) => {
        this.onRPCUpdateSuccessEventEmitter.emit(data);
      },
      (error)=> {
        this.onError(error);
        this.onRPCUpdateFailureEventEmitter.emit();
      });
  }

  sendMachineDeviceSelectionPriceStatusUpdate() {
    let request = new MachineDeviceSelectionPriceStatusUpdateRequest();
    request.costCenterID = Number(this._common.operation.operationNo);
    request.requestStatusCode = 'REQUEST_QUEUED';
    request.userID = this._security.getUserID();
    
    return this._http.post<MachineDeviceSelectionPriceStatusUpdateResponse>(this.appSettings.urlLocations.deviceManagementURL + 'machinedeviceselectionpricestatusupdate', request);
  }

  sendMachineDeviceSelectionPriceRequest(itemsToSend:Array<PogDexDiscrepancySearchItem>, indexToSend:number) {
    if (indexToSend >= itemsToSend.length) {
          // If there are no remaining requests, emit the end-with-success event
          this.onRPCRequestEndEventEmitter.emit();
    } else {
      let item = itemsToSend[indexToSend];
      indexToSend = indexToSend + 1;
      let request = new MachineDeviceSelectionPriceRequest();
      request.setFromItem(item);
      request.userID = this._security.getUserID();
      this._http.post<MachineDeviceSearchResponse>(this.appSettings.urlLocations.deviceManagementURL + 'machinedeviceselectionprice', request)
        .subscribe((data: MachineDeviceSearchResponse) => {
          item.statusCode = "IN_PROCESS";
          this.onRPCRequestChangeEventEmitter.emit(item);
          console.log(`Sent price change request for machine number ${item.machineNumber}`);
          this.sendMachineDeviceSelectionPriceRequest(itemsToSend, indexToSend);
        },
        (error)=> {
          item.statusCode = "ERROR";
          this.onRPCRequestChangeEventEmitter.emit(item);
          let errorMessage = `Failed to send price change request for machine number ${item.machineNumber}.`;
          if (error.error != null && error.error != undefined) {
            errorMessage = errorMessage + ` ${error.error.title}.`            
          }
          console.log(errorMessage);
          this.onError(error);
          this.sendMachineDeviceSelectionPriceRequest(itemsToSend, indexToSend);
        });
    }
  }

  sendMachineDeviceSelectionPriceRequests(itemsToSend:Array<PogDexDiscrepancySearchItem>) {
    this.onRPCRequestBeginEventEmitter.emit();    

    this.sendMachineDeviceSelectionPriceStatusUpdate();
    // Get the first request and provide it recursively to sendMachineDeviceSelectionPriceRequest with array of the remaining requests
    this.sendMachineDeviceSelectionPriceRequest(itemsToSend, 0);
  }

  // --------------------------------------------------------------------------
  // RPC Tracking Status
  GetRPCTrackStatusItems(searchRequest: RpcTrackStatusSearchRequest) {
    return this._http.post<RPCTrackStatusResponse>(this.appSettings.urlLocations.deviceManagementURL + 'rpctrackstatus', searchRequest);
  }

  GetRPCTrackStatusSummary(costCenterID: number) {
    return this._http.get<RPCTrackStatusSummaryResponse>(this.appSettings.urlLocations.deviceManagementURL + 'rpctrackstatussummary?costCenterID=' + costCenterID);
  }

  PutRPCTrackStatusItems(items:RPCTrackStatusItem[], userID:number) {
    const request = new RPCTrackStatusSaveRequest();
    request.items = items;
    request.userId = userID;
    return this._http.put<RPCTrackStatusSaveResponse>(`${this.appSettings.urlLocations.deviceManagementURL}RPCTrackStatus`, request);
  }
  // --------------------------------------------------------------------------

  // --------------------------------------------------------------------------
  // RPC Compatibility
  GetRPCMachineDeviceCompatibilityItems(searchRequest : RPCMachineDeviceCompatibilityItemsRequest) {
    return this._http.post<RPCMachineDeviceCompatibilityItemsResponse>(this.appSettings.urlLocations.deviceManagementURL + 'rpcmachinedevicecompatibility', searchRequest);
  }
  // --------------------------------------------------------------------------
}
